import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthProvider';
import AdminAuthAPI from '../../api/admin/auth';
import PasswordInput from '../../components/PasswordInput';
import './AdminLogin.css';

const AdminLogin = () => {
  // Get admin auth context
  const { login, sendMfaChallenge, completeMfa, isLoading: authLoading, error: authError, clearError } = useAdminAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaState, setMfaState] = useState(null);
  const [mfaMethod, setMfaMethod] = useState('sms_otp');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaPhone, setMfaPhone] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Combine loading states
  const loading = authLoading || mfaLoading;

  // Combine error states
  const error = localError || authError;

  const pickSessionToken = (data = {}) => {
    if (data.intermediate_session_token) return { intermediate_session_token: data.intermediate_session_token };
    if (data.session_token) return { session_token: data.session_token };
    if (data.session_jwt) return { session_jwt: data.session_jwt };
    return {};
  };

  const determineMfaMethod = (payload = {}) => {
    // Handle both camelCase and snake_case from different sources
    const methods = payload.availableMethods || payload.available_methods || [];
    console.log('[AdminLogin] determineMfaMethod - methods:', methods, 'payload:', payload);
    if (methods.length === 1) return methods[0];
    if (methods.includes('sms_otp')) return 'sms_otp';
    if (methods.includes('totp')) return 'totp';
    return 'sms_otp';
  };

  useEffect(() => {
    // Check for magic link token in URL
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkAuth(token);
    }

    // Check for session expiration reason
    const reason = searchParams.get('reason');
    if (reason === 'session_expired' || reason === 'token_expired') {
      setLocalError('Your session has expired. Please log in again.');
    }
  }, [searchParams]);

  // Auto-send TOTP challenge to fetch QR once TOTP is selected
  useEffect(() => {
    if (mfaStep && mfaMethod === 'totp' && !mfaState?.qr_code && !mfaLoading) {
      handleSendMfa();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaStep, mfaMethod]);

  const getQrSrc = () => {
    const qr = mfaState?.qr_code;
    if (!qr) return null;
    return qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`;
  };

  const handleMagicLinkAuth = async (token) => {
    setLocalError('');
    setSuccess('');
    clearError();

    try {
      const result = await AdminAuthAPI.authenticateMagicLink(token);
      setSuccess('Magic link authentication successful! Redirecting...');
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    } catch (error) {
      setLocalError(error.message || 'Magic link authentication failed');
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSuccess('');
    clearError();

    try {
      const result = await login(email, password);

      if (result?.mfaRequired) {
        const data = result.data || {};
        const method = determineMfaMethod(data);
        setMfaMethod(method);
        setMfaState({
          intermediateSessionToken: data.intermediateSessionToken,
          sessionToken: data.sessionToken,
          sessionJwt: data.sessionJwt,
          member_id: data.memberId,
          organization_id: data.organizationId,
          method,
          email: data.email || email,
          role: data.role,
          permissions: data.permissions,
          available_methods: data.availableMethods || ['sms_otp', 'totp'],
          phone_number: data.phone_number
        });
        setMfaPhone(data.phone_number || '');
        setMfaCode('');
        setMfaStep(true);
        return;
      }

      if (result?.success) {
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1500);
      } else {
        setLocalError(result?.error || 'Login failed');
      }
    } catch (error) {
      setLocalError(error.message || 'Login failed');
    }
  };

  const handleSendMfa = async () => {
    if (!mfaState) return;
    setMfaLoading(true);
    setLocalError('');
    setSuccess('');
    clearError();

    try {
      const challenge = await sendMfaChallenge({
        method: mfaMethod,
        organizationId: mfaState.organization_id,
        memberId: mfaState.member_id,
        phoneNumber: mfaMethod === 'sms_otp' ? mfaPhone : undefined,
        intermediateSessionToken: mfaState.intermediateSessionToken,
        sessionToken: mfaState.sessionToken,
        sessionJwt: mfaState.sessionJwt,
        totpRegistrationId: mfaState.totp_registration_id
      });

      if (challenge.success) {
        const data = challenge.data || {};
        setMfaState(prev => ({
          ...prev,
          qr_code: data.qr_code || prev?.qr_code || null,
          totp_registration_id: data.totp_registration_id || prev?.totp_registration_id || null,
          recovery_codes: data.recovery_codes || prev?.recovery_codes || null,
          active_totp: data.active_totp || false
        }));

        // Handle active TOTP (already enrolled)
        if (data.active_totp) {
          setSuccess('TOTP already active. Enter your authenticator code below.');
        } else {
          setSuccess(
            mfaMethod === 'sms_otp'
              ? 'MFA SMS code sent. Please enter the code to continue.'
              : data.qr_code
                ? 'Scan the QR code in your authenticator app, then enter the first code.'
                : 'TOTP enrollment initiated. Enter your authenticator code.'
          );
        }
      } else {
        setLocalError(challenge.error || 'Failed to send MFA challenge');
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to send MFA challenge');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!mfaState || !mfaCode) {
      setLocalError('Please enter the MFA code');
      return;
    }
    setMfaLoading(true);
    setLocalError('');
    setSuccess('');
    clearError();

    try {
      const result = await completeMfa({
        method: mfaMethod,
        code: mfaCode,
        organizationId: mfaState.organization_id,
        memberId: mfaState.member_id,
        intermediateSessionToken: mfaState.intermediateSessionToken,
        sessionToken: mfaState.sessionToken,
        sessionJwt: mfaState.sessionJwt,
        totpRegistrationId: mfaState.totp_registration_id,
        email: mfaState.email,
        role: mfaState.role,
        permissions: mfaState.permissions
      });

      if (result?.success) {
        setSuccess('MFA verified! Redirecting...');
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1000);
      } else {
        setLocalError(result?.error || 'Failed to verify MFA code');
      }
    } catch (err) {
      setLocalError(err.message || 'Failed to verify MFA code');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }

    setLocalError('');
    setSuccess('');
    clearError();

    try {
      const result = await AdminAuthAPI.sendMagicLink(email);
      setMagicLinkSent(true);
      setSuccess('Magic link sent! Please check your email.');
    } catch (error) {
      setLocalError(error.message || 'Failed to send magic link');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }

    setLocalError('');
    setSuccess('');
    clearError();

    try {
      const result = await AdminAuthAPI.sendPasswordReset(email);
      setPasswordResetSent(true);
      setSuccess('Password reset instructions sent! Please check your email.');
    } catch (error) {
      setLocalError(error.message || 'Failed to send password reset email');
    }
  };

  const resetForm = () => {
    setShowMagicLink(false);
    setShowPasswordReset(false);
    setMagicLinkSent(false);
    setPasswordResetSent(false);
    setLocalError('');
    setSuccess('');
    clearError();
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-wrapper">
        <div className="admin-login-header">
          <h1>System Admin Portal</h1>
          <p>Secure access to system administration</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="admin-alert admin-alert-success">
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="admin-alert admin-alert-error">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        {/* Magic Link Success */}
        {magicLinkSent && (
          <div className="admin-auth-success">
            <i className="fas fa-envelope-open"></i>
            <h3>Magic Link Sent!</h3>
            <p>We've sent a secure login link to <strong>{email}</strong></p>
            <p>Please check your email and click the link to continue.</p>
            <button 
              className="admin-btn admin-btn-secondary admin-btn-full" 
              onClick={resetForm}
            >
              Try Different Method
            </button>
          </div>
        )}

        {/* Password Reset Success */}
        {passwordResetSent && (
          <div className="admin-auth-success">
            <i className="fas fa-key"></i>
            <h3>Password Reset Sent!</h3>
            <p>We've sent password reset instructions to <strong>{email}</strong></p>
            <p>Please check your email and follow the instructions to reset your password.</p>
            <button 
              className="admin-btn admin-btn-secondary admin-btn-full" 
              onClick={resetForm}
            >
              Try Different Method
            </button>
          </div>
        )}

        {/* Main Login Form */}
        {!magicLinkSent && !passwordResetSent && (
          <div className="admin-login-form">
            
            {/* Auth Method Selector */}
            <div className="admin-auth-tabs">
              <button 
                className={`admin-auth-tab ${!showMagicLink && !showPasswordReset ? 'active' : ''}`}
                onClick={() => resetForm()}
              >
                <i className="fas fa-key"></i>
                Password
              </button>
              <button 
                className={`admin-auth-tab ${showMagicLink ? 'active' : ''}`}
                onClick={() => { resetForm(); setShowMagicLink(true); }}
              >
                <i className="fas fa-magic"></i>
                Magic Link
              </button>
              <button 
                className={`admin-auth-tab ${showPasswordReset ? 'active' : ''}`}
                onClick={() => { resetForm(); setShowPasswordReset(true); }}
              >
                <i className="fas fa-redo"></i>
                Reset Password
              </button>
            </div>

            {/* Password Login Form */}
            {!showMagicLink && !showPasswordReset && !mfaStep && (
              <form onSubmit={handlePasswordLogin}>
                <div className="admin-form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your admin email"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="admin-form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i>
                    Password
                  </label>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  className="admin-btn admin-btn-primary admin-btn-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Authenticating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt"></i>
                      Sign In
                    </>
                  )}
                </button>
              </form>
            )}

            {/* MFA Step */}
            {mfaStep && mfaState && (
              <div className="admin-mfa-container">
                <h3>Multi-Factor Authentication</h3>
                <p style={{ color: '#555' }}>
                  {mfaState?.available_methods?.length === 1
                    ? `Complete ${mfaMethod === 'sms_otp' ? 'SMS' : 'authenticator app'} verification to continue.`
                    : mfaState?.available_methods?.length === 0
                    ? 'Set up your first MFA method to secure your account.'
                    : 'Select a method and complete MFA to continue.'}
                </p>

                {(mfaState?.available_methods || []).length > 1 && (
                  <div className="admin-form-group">
                    <label>MFA Method</label>
                    <div className="admin-select">
                      <select value={mfaMethod} onChange={(e) => {
                        setMfaMethod(e.target.value);
                        setMfaState(prev => ({
                          ...prev,
                          qr_code: null
                        }));
                        setMfaCode('');
                        setMfaPhone('');
                        setSuccess('');
                        setLocalError('');
                        clearError();
                      }}>
                        {(mfaState?.available_methods || ['sms_otp', 'totp']).map((method) => (
                          <option key={method} value={method}>{method === 'sms_otp' ? 'SMS code' : 'Authenticator app (TOTP)'}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* SMS OTP Flow */}
                {mfaMethod === 'sms_otp' && (
                  <>
                    <div className="admin-form-group">
                      <label>Phone number for SMS</label>
                      <input
                        type="tel"
                        value={mfaPhone}
                        onChange={(e) => setMfaPhone(e.target.value)}
                        placeholder="+1 555 123 4567"
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        onClick={handleSendMfa}
                        disabled={mfaLoading}
                      >
                        {mfaLoading ? 'Sending...' : 'Send Code'}
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-tertiary"
                        onClick={() => setMfaStep(false)}
                        disabled={mfaLoading}
                      >
                        Back
                      </button>
                    </div>

                    <div className="admin-form-group">
                      <label>Enter MFA Code</label>
                      <input
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !mfaLoading && mfaCode) {
                            handleVerifyMfa();
                          }
                        }}
                        placeholder="123456"
                        autoFocus
                      />
                    </div>
                  </>
                )}

                {/* TOTP Flow */}
                {mfaMethod === 'totp' && (
                  <>
                    {getQrSrc() && (
                      <div className="admin-form-group">
                        <label>Scan QR code with your authenticator app</label>
                        <div style={{ padding: '12px', border: '1px solid #eee', borderRadius: '8px', textAlign: 'center' }}>
                          <img
                            src={getQrSrc()}
                            alt="TOTP QR Code"
                            style={{ maxWidth: '240px' }}
                          />
                        </div>
                        {mfaState?.recovery_codes && (
                          <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
                            <strong>Recovery codes:</strong> {mfaState.recovery_codes.join(', ')}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="admin-form-group">
                      <label>Enter code from authenticator app</label>
                      <input
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !mfaLoading && mfaCode) {
                            handleVerifyMfa();
                          }
                        }}
                        placeholder="123456"
                        autoFocus
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <button
                        type="button"
                        className="admin-btn admin-btn-tertiary"
                        onClick={() => setMfaStep(false)}
                        disabled={mfaLoading}
                      >
                        Back
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-btn-full"
                  onClick={handleVerifyMfa}
                  disabled={mfaLoading}
                >
                  {mfaLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </div>
            )}

            {/* Magic Link Form */}
            {showMagicLink && (
              <form onSubmit={handleMagicLinkRequest}>
                <div className="admin-auth-description">
                  <i className="fas fa-info-circle"></i>
                  <p>We'll send a secure login link to your email address. Click the link to access the admin portal without entering a password.</p>
                </div>

                <div className="admin-form-group">
                  <label htmlFor="magic-email">
                    <i className="fas fa-envelope"></i>
                    Email Address
                  </label>
                  <input
                    id="magic-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your admin email"
                    required
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  className="admin-btn admin-btn-primary admin-btn-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Sending Magic Link...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic"></i>
                      Send Magic Link
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Password Reset Form */}
            {showPasswordReset && (
              <form onSubmit={handlePasswordReset}>
                <div className="admin-auth-description">
                  <i className="fas fa-info-circle"></i>
                  <p>We'll send password reset instructions to your email address. Follow the link to create a new password.</p>
                </div>

                <div className="admin-form-group">
                  <label htmlFor="reset-email">
                    <i className="fas fa-envelope"></i>
                    Email Address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your admin email"
                    required
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  className="admin-btn admin-btn-primary admin-btn-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Sending Reset Instructions...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo"></i>
                      Reset Password
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Security Notice */}
            <div className="admin-security-notice">
              <i className="fas fa-shield-alt"></i>
              <p>
                This is a secure admin portal. All access is logged and monitored.
                If you don't have admin credentials, please contact your system administrator.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogin; 
